import { useMutation, useQuery } from '@tanstack/react-query';
import { executeQuery, getPreviewCount, getFilterOptions, getCatalog } from '../api/analyticsApi';
import { useCatalogStore } from '../stores/catalogStore';
import { useOrganizationsStore } from '@/stores/organizationsStore';
import { useEffect } from 'react';
import type { AnalyticsQuery, Filter } from '../types/analytics.types';
import { useAnalyticsStore } from '../stores/analyticsStore';
import { selectVisualization } from '../lib/visualizationSelector';

export function useExecuteQuery() {
  const {
    setUIState,
    setQueryResult,
    setCurrentQuery,
    setSelectedVisualization,
    setError,
  } = useAnalyticsStore();

  return useMutation({
    mutationFn: async ({
      query,
      page,
      pageSize,
    }: {
      query: AnalyticsQuery;
      page?: number;
      pageSize?: number;
    }) => {
      setUIState('loading');
      setError(null);
      const response = await executeQuery(query, page, pageSize);
      return response;
    },
    onSuccess: (data, variables) => {
      setCurrentQuery(variables.query);
      setQueryResult(data.result);
      const visualization = selectVisualization(data.result);
      setSelectedVisualization(visualization);

      if (data.result.data.type === 'list') {
        setUIState('patient_list');
      } else {
        setUIState('results');
      }
    },
    onError: (error) => {
      setError(error instanceof Error ? error.message : 'An error occurred');
      setUIState('idle');
    },
  });
}

export function usePreviewCount(filters: Filter[], enabled = true) {
  const { setPreviewCount, setPreviewLoading } = useAnalyticsStore();

  return useQuery({
    queryKey: ['previewCount', filters],
    queryFn: async () => {
      setPreviewLoading(true);
      try {
        const count = await getPreviewCount(filters);
        setPreviewCount(count);
        return count;
      } finally {
        setPreviewLoading(false);
      }
    },
    enabled: enabled && filters.length > 0,
    staleTime: 5000,
  });
}

export function useFilterOptions(field: string, enabled = true) {
  return useQuery({
    queryKey: ['filterOptions', field],
    queryFn: () => getFilterOptions(field),
    enabled,
    staleTime: 60000,
  });
}

/** Load the backend field catalog for the selected org into the catalog store. */
export function useFilterCatalog() {
  const orgId = useOrganizationsStore((s) => s.selectedOrganizationId);
  const setCatalog = useCatalogStore((s) => s.setCatalog);
  const query = useQuery({
    queryKey: ['analyticsCatalog', orgId],
    queryFn: getCatalog,
    enabled: !!orgId,
    staleTime: 5 * 60 * 1000,
  });
  useEffect(() => {
    if (query.data) setCatalog(query.data);
  }, [query.data, setCatalog]);
  return query;
}
